import { FactoryMoveRequestStatus } from '../enums/factory-move-request-status.enum';

/**
 * Asks shipping to weigh, measure and (optionally) photograph one skid.
 *
 * The skid is the source of truth for the numbers — completing a request writes
 * length/width/height/weight (and any photos) onto the PlasticsSkid itself, so
 * every page that already shows skid dims picks them up. The request keeps its
 * own copy of what was measured, plus frozen order/product facts, so it still
 * reads correctly in history if the skid or order changes later.
 *
 * Status reuses the move-request Open -> Completed/Canceled lifecycle.
 */
export interface PlasticsWeighRequest {
    id: string;
    skidId: string;
    orderId: string;
    orderedProductId: string;
    productId: string;
    productVersionId: string;
    partNumber: string;
    description: string;
    thumbnailImageUrl: string;
    customerId: string;
    customerName: string;
    salesOrderNumber: string;
    purchaseOrderNumber: string;
    skidQuantity: number;
    packingLabelId: string;
    note: string;
    status: FactoryMoveRequestStatus;
    /** Measured values, filled in on completion (inches / pounds). */
    length: number | null;
    width: number | null;
    height: number | null;
    weight: number | null;
    createdOn: Date;
    createdById: string;
    completedOn: Date | null;
    completedById: string;
    canceledOn: Date | null;
    canceledById: string;
}

export interface PlasticsWeighRequestMeasurements {
    length: number;
    width: number;
    height: number;
    weight: number;
}

/** Oldest first — weigh requests are always ASAP, so arrival order is the queue order. */
export function compareOpenPlasticsWeighRequests(
    left: Pick<PlasticsWeighRequest, 'createdOn'>,
    right: Pick<PlasticsWeighRequest, 'createdOn'>,
): number {
    return new Date(left.createdOn).getTime() - new Date(right.createdOn).getTime();
}

export function isCompletePlasticsWeighMeasurement(measurements: Partial<PlasticsWeighRequestMeasurements>): boolean {
    return [measurements.length, measurements.width, measurements.height, measurements.weight]
        .every(value => typeof value === 'number' && Number.isFinite(value) && value > 0);
}

export function formatPlasticsWeighMeasurements(measurements: PlasticsWeighRequestMeasurements): string {
    return `${measurements.length} x ${measurements.width} x ${measurements.height} in, ${measurements.weight} lb`;
}
